// The comms-artery persistence primitive (Brief 5 / point 15).
//
// One tiny, SSR-safe `localStorage` seam shared by the two never-lose-input
// pieces — `usePersistentDraft` (the live composer text) and `outbox` (the
// queued-send store). Kept OUT of `shared/` on purpose: the shared contract
// layer is platform-neutral and never names `localStorage`; this is web app
// code. Every access is guarded (no `window` ⇒ a no-op) and wrapped so a
// disabled / full / private-mode `localStorage` can NEVER throw into a send or
// a keystroke — losing input must be impossible, so the persistence layer fails
// soft and the in-memory state still carries the message.

/** The one namespace every artery key sits under, so a future migration or a
 * "clear my drafts" can find them all and nothing else collides. */
export const ARTERY_PREFIX = "wt_artery";

/** The `localStorage`-backed draft key for one thread/composer. Keyed per
 * thread (point 15: "Keyed per thread") so two open channels never share a
 * draft. The `scope` lets a non-thread composer (e.g. a share guest, or the
 * Global room) carve its own slot under the same namespace. */
export function draftKey(scope: string): string {
  return `${ARTERY_PREFIX}:draft:${scope}`;
}

/** The single outbox key — ONE queue across every thread, so a reconnect flush
 * drains all pending sends in age order regardless of which channel is open. */
export const OUTBOX_KEY = `${ARTERY_PREFIX}:outbox`;

/** True when a real `localStorage` is reachable (browser/jsdom, not SSR). */
function storage(): Storage | null {
  try {
    if (typeof window === "undefined") return null;
    return window.localStorage;
  } catch {
    // Accessing `window.localStorage` itself can throw (sandboxed iframes).
    return null;
  }
}

/** Read a raw string for a key, or null when absent / unavailable. */
export function readRaw(key: string): string | null {
  const s = storage();
  if (!s) return null;
  try {
    return s.getItem(key);
  } catch {
    return null;
  }
}

/** Write a raw string. Fails soft (a full or disabled store never throws). */
export function writeRaw(key: string, value: string): void {
  const s = storage();
  if (!s) return;
  try {
    s.setItem(key, value);
  } catch {
    // Quota exceeded / disabled — the in-memory state remains the source of
    // truth this session; we simply lose the cross-reload backup, never input.
  }
}

/** Remove a key. Fails soft. */
export function removeRaw(key: string): void {
  const s = storage();
  if (!s) return;
  try {
    s.removeItem(key);
  } catch {
    // ignore
  }
}

/** Read + JSON-parse a key into `T`, returning `fallback` on absence or any
 * corruption (a hand-edited or truncated value must degrade to the default,
 * never crash the composer that depends on it). */
export function readJson<T>(key: string, fallback: T): T {
  const raw = readRaw(key);
  if (raw == null) return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

/** JSON-serialise + write a value. Fails soft like `writeRaw`. */
export function writeJson(key: string, value: unknown): void {
  try {
    writeRaw(key, JSON.stringify(value));
  } catch {
    // A value that can't serialise (cyclic) is a programmer error upstream;
    // swallow rather than break the surface.
  }
}

/** A best-effort client idempotency key: `crypto.randomUUID()` where present
 * (every modern browser + jsdom), else a collision-resistant fallback. Used to
 * tag each queued send so a replay can be de-duped (client-side on id, and
 * server-side via the `Idempotency-Key` header once backend dedup ships). */
export function newClientKey(): string {
  try {
    const c = (globalThis as { crypto?: Crypto }).crypto;
    if (c && typeof c.randomUUID === "function") return c.randomUUID();
  } catch {
    // fall through to the manual generator
  }
  return `k-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}
